import React, { memo, useCallback, useMemo, useState, useEffect } from "react";
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  TextField,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useForm } from "react-hook-form";
import axios from "axios";
import toast from "react-hot-toast";
import Loader from "../../components/loader/Loader";
import { useDispatch, useSelector } from "react-redux";
import { showModal } from "../../app/store/slice/counterSlice";
import ImageUpload from "../../components/form-components/ImageUpload";
import AddIcon from "@mui/icons-material/Add";
import { Alert } from "@mui/material";
import { FormInputText } from "../../components/form-components/FormInputText";
import { motion } from "motion/react";

const CreateEditDialog = React.memo(
  ({ open, handleClose, control, errors, handleSubmit, onSubmit }) => {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth={"sm"}>
        <DialogTitle className="text-primary">New Offer Banner</DialogTitle>
        <DialogContent>
          <Grid container columnSpacing={{ xs: 5 }}>
            <Grid item xs={12}>
              <FormInputText
                name="offer_name"
                control={control}
                label="Offer Banner Name"
                errors={errors}
                mandatory="true"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained" color="error">
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            startIcon={<SaveIcon />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

// ✅ BannerItem wrapper so useMemo can be used per banner
// ✅ Fix
const BannerItem = memo(
  ({ banner, platform, loadInitialData, regionId, activeStatus }) => {
    const routeValue = useMemo(
      () => ({
        promo_id: banner?.promo_id,
        custom_promo_id: banner?.custom_promo_id,
        offer_from_date: banner?.offer_from_date,
        offer_to_date: banner?.offer_to_date,
      }),
      [
        banner?.promo_id,
        banner?.custom_promo_id,
        banner?.offer_from_date,
        banner?.offer_to_date,
        activeStatus, // ✅ primitive prop — memo detects change
      ],
    );

    return (
      <Grid item xs={12} md={4} lg={4} xl={4}>
        <ImageUpload
          entity="offer"
          status={activeStatus} // ✅ use primitive prop
          name={banner?.id?.toString()}
          // control={control}
          label={banner?.offer_name}
          type="text"
          defaultValue={
            platform == "app" ? banner?.offer_banner_image : banner?.web_image
          }
          loadInitialData={loadInitialData}
          regionId={regionId || banner?.region_id}
          routeValue={routeValue}
          platform={platform}
          // ✅ routeList not needed for offer entity (no route section)
        />
      </Grid>
    );
  },
);

function OfferUpload({
  regionId,
  loadInitialData,
  routeItems,
  formData,
  loader,
  setLoader,
}) {
  const dispatch = useDispatch();
  const platform = useSelector((state) => state.counter?.platform);
  const [open, setOpen] = useState(false);
  const [heading, setHeading] = useState("");

  // 👈 fetch heading inside component — same as HeroSection
  useEffect(() => {
    axios
      .get("/getAppHomeData/" + regionId + "/null")
      .then((response) => {
        const sections = response?.data?.data?.sections;
        const offerSection = sections?.find((section) => section?.id === 8);
        setHeading(offerSection?.headings);
      })
      .catch((err) => console.error(err));
  }, [regionId]);

  const methods = useForm();
  const {
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = methods;

  const onSubmit = (data) => save(data);

  const save = async (data) => {
    setLoader(true);
    try {
      const payload = { ...data, region_id: regionId };
      const response = await axios.post("/offer", payload);
      if (!response?.data?.error) {
        toast.success(response?.data?.message);
        loadInitialData();
        reset();
      } else {
        let errDesc = "";
        if (response?.data?.data) {
          Object.values(response?.data?.data).map((error, index) => {
            errDesc =
              index === 0 ? errDesc + error + "\n " : errDesc + error + "\n ";
          });
        } else {
          errDesc = response?.data?.message;
        }
        dispatch(showModal({ msg: errDesc, error: response?.data?.error }));
      }
    } catch (error) {
      dispatch(showModal({ msg: error?.message, error: true }));
    } finally {
      setOpen(false);
      setLoader(false);
    }
  };
  // 2. Add saveHeading function inside OfferUpload:
  const saveHeading = async (headingValue) => {
    if (!headingValue) return;
    try {
      const sectionId = 8; // ← offer section_id from your API data
      const response = await axios.get(
        `/updateSectionHeading/${sectionId}/${headingValue}/${regionId}`,
      );
      if (!response?.data?.error) {
        toast.success(response?.data?.message);
        loadInitialData();
      } else {
        dispatch(showModal({ msg: response?.data?.message, error: true }));
      }
    } catch (error) {
      dispatch(showModal({ msg: error?.message, error: true }));
    }
  };
  const handleClose = useCallback(() => setOpen(false), []);

  const offersBasedOnPlatform = (formData?.length > 0 && formData) || [];
  //   useEffect(() => {
  //   if (headings) setHeading(headings);
  // }, [headings]);

  return loader ? (
    <Loader />
  ) : (
    <>
      <Grid item xs={12}>
        <ul
          className="w-100 d-inline-flex justify-content-around"
          style={{ color: "red", marginTop: "8px" }}
        >
          {formData?.length <= 8 && (
            <Button
              size="small"
              variant="contained"
              onClick={() => setOpen(true)}
              icon={<AddIcon />}
            >
              New Offer
            </Button>
          )}
          <li>Please upload only jpg, jpeg, png.</li>
          <li>File size shall not exceed 300kb</li>
          <li>Dimension : {platform == "app" ? "4:5 ratio" : "4:3"}</li>
        </ul>
      </Grid>
      <Grid item xs={12}>
        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={5}>
              <TextField
                fullWidth
                label="Heading"
                variant="outlined"
                value={heading}
                onChange={(e) => setHeading(e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={3}>
              <Button
                color="primary"
                variant="contained"
                onClick={() => saveHeading(heading)} // 👈 direct local state
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid
        container
        spacing={2}
        columnSpacing={{ xs: 5 }}
        style={{ display: "flex" }}
      >
        {offersBasedOnPlatform?.length > 0 ? (
          offersBasedOnPlatform?.map((banner) => (
            <BannerItem
              key={`${banner?.id}-${banner?.active_status}`}
              banner={banner}
              platform={platform}
              loadInitialData={loadInitialData}
              regionId={regionId}
              activeStatus={banner?.active_status} // ✅ ADD THIS
            />
          ))
        ) : (
          <Grid item xs={12}>
            <Alert severity="warning">No Offers Found !!</Alert>
          </Grid>
        )}
      </Grid>
      <CreateEditDialog
        open={open}
        handleClose={handleClose}
        control={control}
        errors={errors}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
      />
    </>
  );
}

export default OfferUpload;
